import React, { useRef, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ApprovalDocumentPreview } from './ApprovalDocumentPreview';
import type { ApprovalDocumentModalProps } from './approvalDocument.types';

export const ApprovalDocumentModal: React.FC<ApprovalDocumentModalProps> = ({ isOpen, onClose, data }) => {
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useCallback(() => {
    if (!printRef.current) return;

    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '_blank', 'width=800,height=600');

    if (!printWindow) {
      alert('Please allow pop-ups to print the document.');
      return;
    }

    const printStyles = `
      /* Multi-page support with proper pagination */
      @page {
        size: A4 portrait;
        margin: 10mm 0;
      }

      * {
        box-sizing: border-box;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }

      body {
        margin: 0;
        padding: 0;
        font-family: Arial, Helvetica, sans-serif;
      }

      /* Main document table - enables repeating header/footer */
      #approval-document {
        width: 100%;
        background-color: #ffffff !important;
        font-family: Arial, Helvetica, sans-serif;
        font-size: 10pt;
        line-height: 1.4;
        color: #1a1a1a;
        border-collapse: collapse;
      }

      /* CRITICAL: Repeating header on every page */
      #approval-document > thead.document-header {
        display: table-header-group;
      }

      /* CRITICAL: Repeating footer on every page */
      #approval-document > tfoot.document-footer {
        display: table-footer-group;
      }

      /* Header cell styling */
      #approval-document > thead.document-header th {
        padding: 15mm 20mm 10mm 20mm;
        vertical-align: top;
        border: none;
        background: none;
      }

      /* Footer cell styling */
      #approval-document > tfoot.document-footer td {
        padding: 10mm 20mm 15mm 20mm;
        vertical-align: bottom;
        border: none;
      }

      /* Main content cell styling */
      #approval-document > tbody > tr > td {
        padding: 0 20mm;
        vertical-align: top;
        border: none;
      }

      /* Ensure backgrounds print */
      [style*="background"] {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }

      /* Table styles for nested tables (workflow, consolidated items) */
      table table {
        width: 100%;
        border-collapse: collapse;
      }

      table table th,
      table table td {
        padding: 8px 10px;
        border-bottom: 1px solid #e2e8f0;
      }

      table table th {
        background-color: #f1f5f9 !important;
        font-weight: 600;
        text-align: left;
      }

      /* Page break control */
      .avoid-break {
        page-break-inside: avoid;
        break-inside: avoid;
      }

      /* Stamp image styles for print - ensure proper rendering on all pages */
      img {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        display: block !important;
      }

      /* SVG styles for any remaining SVG elements */
      svg {
        display: inline-block !important;
        overflow: visible !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }

      /* Workflow and consolidated tables - allow breaks but keep header visible */
      .workflow-section table,
      .consolidated-section table {
        page-break-inside: auto;
        break-inside: auto;
      }

      .workflow-section thead,
      .consolidated-section thead {
        display: table-header-group;
      }

      .workflow-section tr,
      .consolidated-section tr {
        page-break-inside: avoid;
        break-inside: avoid;
      }

      /* Attachment items should not break */
      .attachments-section .avoid-break {
        page-break-inside: avoid;
        break-inside: avoid;
      }

      /* Orphan and widow control for paragraphs */
      p {
        orphans: 3;
        widows: 3;
      }

      /* Section titles should stay with content */
      h3 {
        page-break-after: avoid;
        break-after: avoid;
      }

      /* Hide page break indicators in print */
      .page-break-indicator {
        display: none !important;
      }
    `;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${data.metadata.documentTitle} - ${data.metadata.documentId}</title>
          <style>${printStyles}</style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);

    printWindow.document.close();

    // Wait for content to load before printing
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.onafterprint = () => {
        printWindow.close();
      };
    };

    // Fallback for browsers that don't support onafterprint
    setTimeout(() => {
      if (!printWindow.closed) {
        printWindow.print();
      }
    }, 250);
  }, [data.metadata.documentTitle, data.metadata.documentId]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
    // Ctrl/Cmd + P to print
    if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
      e.preventDefault();
      handlePrint();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '1rem',
      }}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div
        style={{
          background: 'var(--bg-primary, #ffffff)',
          borderRadius: '0.5rem',
          width: '100%',
          maxWidth: '900px',
          maxHeight: '95vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: '1rem 1.5rem',
            borderBottom: '1px solid var(--border-color, #e2e8f0)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'var(--bg-secondary, #f8fafc)',
            borderTopLeftRadius: '0.5rem',
            borderTopRightRadius: '0.5rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '0.5rem',
                backgroundColor: '#1e40af',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
              }}
            >
              <IconSystem.file size={20} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: 'var(--text-primary, #1e293b)' }}>
                Approval Document Preview
              </h2>
              <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-secondary, #64748b)' }}>
                {data.metadata.documentId}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              onClick={handlePrint}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                backgroundColor: '#1e40af',
                color: '#ffffff',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1e3a8a';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#1e40af';
              }}
              title="Print document (Ctrl+P)"
            >
              <IconSystem.print size={16} />
              Print
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0.5rem',
                color: 'var(--text-secondary, #64748b)',
                borderRadius: '0.25rem',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--bg-tertiary, #e2e8f0)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              title="Close (Esc)"
            >
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        {/* Document Preview Container */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: '1.5rem',
            backgroundColor: '#64748b',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <div
            ref={printRef}
            style={{
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
              backgroundColor: '#ffffff',
            }}
          >
            <ApprovalDocumentPreview data={data} showPageBreaks={true} />
          </div>
        </div>

        {/* Modal Footer */}
        <div
          style={{
            padding: '1rem 1.5rem',
            borderTop: '1px solid var(--border-color, #e2e8f0)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'var(--bg-secondary, #f8fafc)',
            borderBottomLeftRadius: '0.5rem',
            borderBottomRightRadius: '0.5rem',
          }}
        >
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted, #94a3b8)' }}>
            Press <kbd style={{ padding: '2px 6px', backgroundColor: '#e2e8f0', borderRadius: '4px' }}>Ctrl</kbd> +{' '}
            <kbd style={{ padding: '2px 6px', backgroundColor: '#e2e8f0', borderRadius: '4px' }}>P</kbd> to print
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={handlePrint}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1.25rem',
                backgroundColor: '#1e40af',
                color: '#ffffff',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1e3a8a';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#1e40af';
              }}
            >
              <IconSystem.print size={16} />
              Print Document
            </button>
            <button
              onClick={onClose}
              style={{
                padding: '0.5rem 1.25rem',
                backgroundColor: 'transparent',
                color: 'var(--text-primary, #1e293b)',
                border: '1px solid var(--border-color, #e2e8f0)',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--bg-tertiary, #e2e8f0)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovalDocumentModal;
